import { useTranslations } from 'next-intl';
import { type FC, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form/dist/types/form';

import { zodResolver } from '@hookform/resolvers/zod';

import { z } from 'zod';

import { Flex } from '@/components/common/Flex';
import { FormSubmitButton } from '@/components/common/form/FormSubmitButton';
import { FormTextInput } from '@/components/common/form/FormTextInput';
import { Text } from '@/components/common/Text';
import { TextLink } from '@/components/common/TextLink';
import { urls } from '@/constants/urls';

import styles from './RegularAuth.module.scss';

type RegularAuthProps = {
  onSubmit: RegularAuthFormHandler;
};

const useFormSchema = () => {
  const tErrors = useTranslations('errors');

  return useMemo(
    () =>
      z.object({
        username: z.string().trim().min(1, tErrors('field is required')).toLowerCase(),
        password: z.string().min(1, tErrors('field is required')),
      }),
    [tErrors]
  );
};

export type RegularAuthFormData = z.TypeOf<ReturnType<typeof useFormSchema>>;

export type RegularAuthFormHandler = SubmitHandler<RegularAuthFormData>;

export const RegularAuth: FC<RegularAuthProps> = (props) => {
  const t = useTranslations('pages.account.login.form.regularAuthBlock');

  const { onSubmit } = props;

  const form = useForm<RegularAuthFormData>({
    resolver: zodResolver(useFormSchema()),
    mode: 'onBlur',
  });

  const { register, handleSubmit } = form;

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex direction="column" gap={9}>
          <Flex className={styles.titleBlock} direction="column" gap={4}>
            <Text type="medium64" whiteSpace="preWrap">
              {t('title')}
            </Text>

            <Text type="regular18" color="white-50p">
              {t('subtitle')}
            </Text>
          </Flex>

          <Flex className={styles.formContainer} direction="column">
            <Flex direction="column" gap={1}>
              <FormTextInput
                {...register('username')}
                type="email"
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="username"
                required
                placeholder={t('fields.username.placeholder')}
              />

              <FormTextInput
                {...register('password')}
                type="password"
                placeholder={t('fields.password.placeholder')}
                autoComplete="new-password"
              />
            </Flex>

            <TextLink className={styles.passwordRecoveryLink} href={urls.PasswordRecovery}>
              {t('passwordRecoveryLink')}
            </TextLink>

            <Flex direction="column" gap={4}>
              <FormSubmitButton>{t('submitButton')}</FormSubmitButton>

              <Text className={styles.dontHaveAccount} type="regular14" color="white-50p">
                {t.rich('alreadyHaveAccount', {
                  'signup-link': (children) => <TextLink href={urls.Registration}>{children}</TextLink>,
                })}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </form>
    </FormProvider>
  );
};
